@use '@/styles/tokens' as *;

// Figma Toast (node 193-3750). Light bg comes from the Notification theme
// token (surface.page); dark appearance overrides it here.

.toast {
  // The pill's own colour, as bare channels, so the scrim below can fade it to 30%.
  --toast-scrim: 252 252 247; // surface.page

  border-radius: 16px;
  box-shadow: var(--shadow-overlay);

  .title,
  .description {
    word-break: break-word;
  }
}

// Figma 2455:323414 — the 80px scrim the close control sits on, pinned to the pill's
// right edge and fading the message out beneath it. It is the pill's own colour, held
// solid across its outer half and dropping to 30% at its inner edge, so the text does
// not stop abruptly at the icon. `pointer-events: none` keeps the message hoverable
// through it; `z-index: 0` puts it over the message but under the close control and the
// action slot, which claim `z-index: 1` below — the whole point is that it covers the
// text and nothing else. No `position: relative` is needed on the pill — antd already lays
// each notice out `position: absolute`, which is the containing block this anchors to.
//
// It must be `::before`. antd already owns the notice's `::after` — a transparent 16px
// bridge above each notice (`top: -margin; height: margin; inset-inline: 0`) that keeps a
// stack hoverable across the gaps. A second `::after` here is the *same* element, so the
// two rule sets merge: antd's geometry won and this gradient painted as an 80px black bar
// floating above the pill.
.toast::before {
  content: '';
  position: absolute;
  z-index: 0;
  inset-block: 0;
  inset-inline-end: 0;
  width: 80px;
  pointer-events: none;
  background: linear-gradient(
    to left,
    rgb(var(--toast-scrim)) 0%,
    rgb(var(--toast-scrim)) 50%,
    rgb(var(--toast-scrim) / 30%) 100%
  );
  border-start-end-radius: 16px;
  border-end-end-radius: 16px;
}

.actions {
  position: relative;
  z-index: 1;
}

// Figma 2455:323403 — an outlined X-circle, vertically centred against the pill's
// right edge. It is hidden at rest and revealed on hover: `opacity`, not `visibility`,
// so the control stays tabbable, and `:focus-within` keeps it lit while it holds focus —
// the same idiom `<DataTable>`'s `revealOnHover` action cell uses. The rule sits behind
// `@media (hover: hover)` because a device that cannot hover has no gesture to reveal it,
// so there the control is simply always visible.
//
// The glyph is 22px in a 28px round button, so the hover wash reads as a disc around the
// icon rather than a square behind it. The button is centred on the glyph's own centre —
// 24px in from the pill's edge — hence a 10px inset.
//
// antd's own `.ant-notification .ant-notification-notice-close` ties this selector on
// specificity and is injected at runtime, so the four properties it also sets carry
// `!important` — the same escape `.dark`'s background already takes.
.toast .closeButton {
  top: 50% !important;
  inset-inline-end: 10px !important;
  width: 28px !important;
  height: 28px !important;
  z-index: 1;
  transform: translateY(-50%);
  cursor: pointer;
  border-radius: 50% !important;

  &:hover {
    background: rgb(0 0 0 / 6%) !important;
  }

  &:active {
    background: rgb(0 0 0 / 10%) !important;
  }
}

.closeIcon {
  display: block;
  color: $color-neutral-400;
  transition: color 0.15s ease;
}

.closeButton:hover .closeIcon,
.closeButton:focus-visible .closeIcon {
  color: var(--text-primary);
}

@media (hover: hover) {
  .toast::before,
  .toast .closeButton {
    opacity: 0;
    // Restates background-color because this shorthand replaces antd's own transition.
    transition:
      opacity 0.15s ease,
      background-color 0.15s ease;
  }

  .toast:hover::before,
  .toast:focus-within::before,
  .toast:hover .closeButton,
  .toast:focus-within .closeButton {
    opacity: 1;
  }
}

.title {
  font-size: 14px;
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.title.success {
  color: $color-brand-400;
}

.title.warning {
  color: $color-warning-700;
}

.title.error {
  color: $color-error-700;
}

.description {
  font-size: 13px;
  color: var(--text-muted);
}

.icon {
  color: var(--text-primary);
}

.icon.success {
  color: $color-brand-400;
}

.icon.warning {
  color: $color-warning-700;
}

.icon.error {
  color: $color-error-700;
}

.dark {
  --toast-scrim: 17 17 17; // neutral.950

  background: $color-neutral-950 !important;

  .title {
    color: var(--text-inverse);
  }

  .title.success {
    color: $color-brand-400;
  }

  .title.warning {
    color: $color-warning-700;
  }

  .title.error {
    color: $color-error-700;
  }

  .description {
    color: var(--text-disabled);
  }

  .icon {
    color: var(--text-inverse);
  }

  .icon.success {
    color: $color-brand-400;
  }

  .icon.warning {
    color: $color-warning-700;
  }
  .icon.error {
    color: $color-error-700;
  }

  .closeButton:hover {
    background: rgb(255 255 255 / 12%) !important;
  }

  .closeButton:active {
    background: rgb(255 255 255 / 18%) !important;
  }

  .closeButton:hover .closeIcon,
  .closeButton:focus-visible .closeIcon {
    color: var(--text-inverse);
  }
}

:global {
  .ant-notification {
    .ant-notification-notice {
      width: fit-content;
    }
  }
}
